import { sumObjectArrayProperty } from '../utils/sumObjectArrayProperty'
import { doesQualifyToBeHidden } from './ItemAvailabilityStatus'
import {
  getUniqueSuppliersFromMSServices,
  isMSServiceWithProductLevelItem,
} from './MSService'
import { countUniqueStatii } from './StatiiCounts'
import { getTermBreakdown } from './TermBreakdown'
import { countUniqueLocations } from './LocationCounts'
import { propagateOrderServiceOrderData } from './OrderServiceOrder' // PART OF KLUDGE. JR.
import {
  getCountableMSServiceQuantity,
  getCountableMSServices,
  isCountableMSService,
  isSupersededMSService,
  sumCountableMSServicesQuantity,
} from './MSServices'
import type {
  IOrder,
  IOrderMasterService,
  IOrderOverview,
  IOrderServiceOrder,
} from '~/types/order/models'
import type { IEntityDataItemWithStatus, TEntityId } from '~/types/models'
import type { TMSService } from '~/types/msService/models'
import type { IContract } from '~/types/contract/models'
import { assertIsString } from '~/types/models'
import {
  OrderMasterServicesStatusIds,
  getOrderMasterServiceStatusById,
} from '~/services/selectLists/orderMasterServicesStatii'
import {
  OrderStatusIds,
  getOrderStatusById,
} from '~/services/selectLists/orderStatii'
import { OrderMSServicesStatusIds } from '~/services/selectLists/orderMSServicesStatii'
import {
  ContractStatusIds,
  getContractStatusById,
} from '~/services/selectLists/contractStatii'
import { ContractTypeIds } from '~/services/selectLists/contractTypes'
import {
  OrderServiceOrderStatusIds,
  getOrderServiceOrderStatusById,
} from '~/services/selectLists/orderServiceOrderStatii'

const mn = '[cynqueModules/dataObjects/OrderMSService.ts]'

interface TPotentialStatus {
  // `number`, not the boxed `Number` — the sibling fields below already use the
  // primitive, and `Number` is the wrapper-object type (no arithmetic, flagged
  // by ban-types).
  statusId: number
  didFindStatus?: boolean
  didFindOtherThanStatusOrCancelled?: boolean
  statusIdForOnlyMssStatus: number
  statusIdForOtherFound: number
}

/**
 * Group the COUNTABLE msServices by some owning entity's id, in one pass.
 *
 * Both the contracts stage and the order-service-orders stage used to filter
 * the whole msService array once per entity, which is O(entities x msServices)
 * twice over in the same cascade. `keyOf` returning undefined means "belongs to
 * no such entity" and the msService is skipped.
 */
function groupCountableByKey(
  msServices: TMSService[],
  keyOf: (msService: TMSService) => TEntityId | undefined
): Map<TEntityId, TMSService[]> {
  const grouped = new Map<TEntityId, TMSService[]>()
  for (const msService of msServices) {
    const key = keyOf(msService)
    if (key === undefined || !isCountableMSService(msService)) {
      continue
    }
    const bucket = grouped.get(key)
    if (bucket) {
      bucket.push(msService)
    } else {
      grouped.set(key, [msService])
    }
  }
  return grouped
}

/**
 * The msServices whose amounts roll up into a parent: included, and not
 * CANCELLED. Extracted so the order-level and master-service-level roll-ups
 * filter once and share the subset across their sums.
 *
 * ⚠ Terminal status here is CANCELLED — DISCONNECTED in the Company cascade,
 * CLOSED in the Opportunity one. The three silos' status ids are unrelated
 * number spaces.
 *
 * ⚠ Also excludes every row that qualifies to be hidden — not available,
 * declined, swapped out, removed. A hidden msService keeps its amounts, so
 * without this the money it represents keeps counting on a row that is not part
 * of the configuration.
 *
 * 🔑 `doesQualifyToBeHidden`, NOT `isSupersededMSService`. Those differ by the
 * two legacy states — NOT_AVAILABLE and OPTIONAL_NOT_INCLUDED — and this path
 * used to admit both, while the kit and card totals excluded them. So the two
 * client money paths disagreed with each other about a declined row, and the
 * narrower test here is what made that true. The API's amount views take the
 * same widening, so client and server agree on one rule: hidden is not counted.
 *
 * ⚠ WHAT THIS DOES AND DOES NOT MOVE. MRC does not move at all: a hidden row is
 * quantity-zeroed by `deriveMSServiceQuantity` and mrcAmount is quantity x rate,
 * so it was already contributing zero. NRC, one-time and TCV are the real delta
 * — none of them passes through quantity (nrcAmount is `1 x unitNrcAmount`
 * whenever doMultiplyNrcByQuantity is false, oneTimeAmount is stored outright,
 * and tcvAmount collapses to oneTimeAmount once mrcAmount is zero). ⚠ The mock
 * fixture has every unitNrcAmount and oneTimeAmount at zero, so a green suite
 * says nothing here; the delta has to be measured against real data.
 */
function getMSServicesToRollUp(msServices: TMSService[]): TMSService[] {
  return msServices.filter(
    (momss) =>
      momss.doInclude &&
      momss.status.id !== OrderMSServicesStatusIds.CANCELLED &&
      !doesQualifyToBeHidden(momss)
  )
}

export function propagateMOMSServicesData({
  overviewData,
  allMOMSServices,
  setUpOrderMSServices,
}: {
  overviewData: IOrderOverview
  allMOMSServices: TMSService[]
  setUpOrderMSServices: Function
}) {
  // embedded functions
  function _propagateFromMOMSServicesToOrder({
    mn,
    order,
    allMOMSServices,
  }: {
    mn: string
    order: IOrder
    allMOMSServices: TMSService[]
  }) {
    function __getRecursPaidDescr(allMOMSServices: TMSService[]) {
      // One pass, short-circuiting on the first disagreement. This used to
      // `find` the reference value and then re-`filter` the whole array with the
      // identical predicate before `every`.
      let firstValidRecursPaidDescr: string | undefined
      for (const momss of allMOMSServices) {
        if (
          !momss.doInclude ||
          momss.recursPaidDescr === '--' ||
          doesQualifyToBeHidden(momss)
        ) {
          continue
        }
        if (firstValidRecursPaidDescr === undefined) {
          firstValidRecursPaidDescr = momss.recursPaidDescr
        } else if (momss.recursPaidDescr !== firstValidRecursPaidDescr) {
          return 'Varies'
        }
      }
      return firstValidRecursPaidDescr ?? '--'
    }

    //
    // Execution starts here
    //

    const fn = `${mn} _propagateFromMOMSServicesToOrder |`

    // Make sure we have some services to propagate from
    if (!allMOMSServices?.length) {
      return
    }
    const didFindFirstMOMSSWIthProductLevelItem =
      allMOMSServices.find(isMSServiceWithProductLevelItem) != undefined
    if (!didFindFirstMOMSSWIthProductLevelItem) {
      return
    }

    // Propagate Status
    const propagateStatusArray: TPotentialStatus[] = [
      {
        statusId: OrderMSServicesStatusIds.IN_REVIEW,
        statusIdForOnlyMssStatus: OrderStatusIds.IN_REVIEW,
        statusIdForOtherFound: OrderStatusIds.SOME_IN_REVIEW,
      },
      {
        statusId: OrderMSServicesStatusIds.READY_FOR_CONTRACT,
        statusIdForOnlyMssStatus: OrderStatusIds.READY_FOR_CONTRACTS,
        statusIdForOtherFound: OrderStatusIds.SOME_READY_FOR_CONTRACTS,
      },
      {
        statusId: OrderMSServicesStatusIds.PREORDER_UNLOCKED,
        statusIdForOnlyMssStatus: OrderStatusIds.PREORDER,
        statusIdForOtherFound: OrderStatusIds.SOME_PREORDER,
      },
      {
        statusId: OrderMSServicesStatusIds.PREORDER_LOCKED,
        statusIdForOnlyMssStatus: OrderStatusIds.PREORDER,
        statusIdForOtherFound: OrderStatusIds.SOME_PREORDER,
      },
      {
        statusId: OrderMSServicesStatusIds.READY_FOR_SERVICEORDER,
        statusIdForOnlyMssStatus: OrderStatusIds.READY_FOR_SERVICEORDERS,
        statusIdForOtherFound: OrderStatusIds.SOME_READY_FOR_SERVICEORDERS,
      },
      {
        statusId: OrderMSServicesStatusIds.SERVICEORDER_IN_PROGRESS,
        statusIdForOnlyMssStatus: OrderStatusIds.SERVICEORDERS_IN_PROGRESS,
        statusIdForOtherFound: OrderStatusIds.SOME_SERVICEORDERS_IN_PROGRESS,
      },
      {
        statusId: OrderMSServicesStatusIds.ACCEPTED,
        statusIdForOnlyMssStatus: OrderStatusIds.ACCEPTED,
        statusIdForOtherFound: OrderStatusIds.SOME_ACCEPTED,
      },
      {
        statusId: OrderMSServicesStatusIds.COMPLETE,
        statusIdForOnlyMssStatus: OrderStatusIds.COMPLETE,
        statusIdForOtherFound: OrderStatusIds.SOME_COMPLETE,
      },
      {
        statusId: OrderMSServicesStatusIds.CANCELLED,
        statusIdForOnlyMssStatus: OrderStatusIds.CANCELLED,
        statusIdForOtherFound: OrderStatusIds.SOME_CANCELLED,
      },
    ]

    order.status = getTargetEntityStatusFromPropagateStatusArray<IOrder>({
      propagateStatusArray,
      momssArray: allMOMSServices,
      targetEntity: order,
      targetEntityGetStatusByIdFunction: getOrderStatusById,
    })

    // Propagate other than status.
    // All four amounts share one predicate, so filter ONCE and sum the subset
    // rather than re-filtering the whole array inside each sum.
    const momsServicesToRollUp = getMSServicesToRollUp(allMOMSServices)
    order.mrcAmount = sumObjectArrayProperty(
      fn,
      momsServicesToRollUp,
      'mrcAmount'
    )
    console.debug(`${fn} mrcAnount: ${order.mrcAmount}`)
    order.nrcAmount = sumObjectArrayProperty(
      fn,
      momsServicesToRollUp,
      'nrcAmount'
    )
    order.oneTimeAmount = sumObjectArrayProperty(
      fn,
      momsServicesToRollUp,
      'oneTimeAmount'
    )
    order.tcvAmount = sumObjectArrayProperty(
      fn,
      momsServicesToRollUp,
      'tcvAmount'
    )

    order.recursPaidDescr = __getRecursPaidDescr(allMOMSServices)
    order.suppliers = getUniqueSuppliersFromMSServices(allMOMSServices)

    const filteredOrderMSServices = getCountableMSServices(allMOMSServices)
    order.numOrderMSServices = sumCountableMSServicesQuantity(
      filteredOrderMSServices
    )
    order.momssStatusCounts = countUniqueStatii(
      filteredOrderMSServices,
      getCountableMSServiceQuantity
    )
  }
  function _propagateFromMOMSServicesToMOMS({
    mn,
    allMOMS,
  }: {
    mn: string
    allMOMS: IOrderMasterService[]
  }) {
    const fn = `${mn} _propagateFromMOMSServicesToMOMS |`
    allMOMS.forEach((moms: IOrderMasterService) => {
      console.debug(`${fn} moms: ${moms.descr}`)

      // Make sure we have some services to propagate from
      const indexOfFirstMOMSSWIthProductLevelItem =
        moms.orderMSServices.findIndex(isMSServiceWithProductLevelItem)
      if (indexOfFirstMOMSSWIthProductLevelItem === -1) {
        moms.term = '--'
        moms.termBreakdown = undefined
        moms.status = getOrderMasterServiceStatusById(
          OrderMasterServicesStatusIds.NO_SERVICES
        )
        return
      }

      // Propagate Status
      const propagateStatusArray: TPotentialStatus[] = [
        {
          statusId: OrderMSServicesStatusIds.IN_REVIEW,
          statusIdForOnlyMssStatus: OrderMasterServicesStatusIds.IN_REVIEW,
          statusIdForOtherFound: OrderMasterServicesStatusIds.SOME_IN_REVIEW,
        },
        {
          statusId: OrderMSServicesStatusIds.READY_FOR_CONTRACT,
          statusIdForOnlyMssStatus:
            OrderMasterServicesStatusIds.READY_FOR_CONTRACTS,
          statusIdForOtherFound:
            OrderMasterServicesStatusIds.SOME_READY_FOR_CONTRACTS,
        },
        {
          statusId: OrderMSServicesStatusIds.PREORDER_UNLOCKED,
          statusIdForOnlyMssStatus: OrderMasterServicesStatusIds.PREORDER,
          statusIdForOtherFound: OrderMasterServicesStatusIds.SOME_PREORDER,
        },
        {
          statusId: OrderMSServicesStatusIds.PREORDER_LOCKED,
          statusIdForOnlyMssStatus: OrderMasterServicesStatusIds.PREORDER,
          statusIdForOtherFound: OrderMasterServicesStatusIds.SOME_PREORDER,
        },
        {
          statusId: OrderMSServicesStatusIds.READY_FOR_SERVICEORDER,
          statusIdForOnlyMssStatus:
            OrderMasterServicesStatusIds.READY_FOR_SERVICEORDERS,
          statusIdForOtherFound:
            OrderMasterServicesStatusIds.SOME_READY_FOR_SERVICEORDERS,
        },
        {
          statusId: OrderMSServicesStatusIds.SERVICEORDER_IN_PROGRESS,
          statusIdForOnlyMssStatus:
            OrderMasterServicesStatusIds.SERVICEORDERS_IN_PROGRESS,
          statusIdForOtherFound:
            OrderMasterServicesStatusIds.SOME_SERVICEORDERS_IN_PROGRESS,
        },
        {
          statusId: OrderMSServicesStatusIds.ACCEPTED,
          statusIdForOnlyMssStatus: OrderMasterServicesStatusIds.ACCEPTED,
          statusIdForOtherFound: OrderMasterServicesStatusIds.SOME_ACCEPTED,
        },
        {
          statusId: OrderMSServicesStatusIds.COMPLETE,
          statusIdForOnlyMssStatus: OrderMasterServicesStatusIds.COMPLETE,
          statusIdForOtherFound: OrderMasterServicesStatusIds.SOME_COMPLETE,
        },
        {
          statusId: OrderMSServicesStatusIds.CANCELLED,
          statusIdForOnlyMssStatus: OrderMasterServicesStatusIds.CANCELLED,
          statusIdForOtherFound: OrderMasterServicesStatusIds.SOME_CANCELLED,
        },
      ]

      moms.status =
        getTargetEntityStatusFromPropagateStatusArray<IOrderMasterService>({
          propagateStatusArray,
          momssArray: moms.orderMSServices,
          targetEntity: moms,
          targetEntityGetStatusByIdFunction: getOrderMasterServiceStatusById,
        })

      // Propagate other than status
      // Counted over exactly the # Srvs set, so the Term hover's counts add up
      // to that column. `term` stays 'Varies' when they differ; the card
      // shows `termBreakdown.majorityTerm`.
      const termBreakdown = getTermBreakdown(
        getCountableMSServices(moms.orderMSServices)
      )
      moms.term = termBreakdown.term
      moms.termBreakdown = termBreakdown.doesVary ? termBreakdown : undefined
      const momsServicesToRollUp = getMSServicesToRollUp(moms.orderMSServices)
      moms.mrcAmount = sumObjectArrayProperty(
        fn,
        momsServicesToRollUp,
        'mrcAmount'
      )
      console.debug(`${fn} mrcAnount: ${moms.mrcAmount}`)
      moms.nrcAmount = sumObjectArrayProperty(
        fn,
        momsServicesToRollUp,
        'nrcAmount'
      )
      moms.oneTimeAmount = sumObjectArrayProperty(
        fn,
        momsServicesToRollUp,
        'oneTimeAmount'
      )

      // status counts — scoped to THIS master service's own MS services.
      // `allMOMSServices` is the flat list across every master service on the
      // order, so using it here gave each master service the order-wide counts
      // and made them all identical. Every sibling roll-up in this same loop
      // (term, mrcAmount, nrcAmount, oneTimeAmount) already uses
      // `moms.orderMSServices`, and both other silos scope it correctly too —
      // CompanyMSService uses `cms.companyMSServices`, OpportunityMSService
      // uses the per-OMS array.
      const filteredMOMSServices = getCountableMSServices(moms.orderMSServices)
      moms.momssStatusCounts = countUniqueStatii(
        filteredMOMSServices,
        getCountableMSServiceQuantity
      )
      moms.momssLocationCounts = countUniqueLocations(
        filteredMOMSServices,
        getCountableMSServiceQuantity
      )
    })
  }
  function _propagateFromMOMSServicesToContracts({
    mn,
    allContracts,
    allMOMSServices,
  }: {
    mn: string
    allContracts: IContract[]
    allMOMSServices: TMSService[]
  }) {
    const fn = `${mn} _propagateFromMOMSServicesToContracts |`

    // Index the countable msServices by contract id in ONE pass. This used to
    // filter the entire array once per contract.
    const countableByContractId = groupCountableByKey(
      allMOMSServices,
      (momss) => momss.contract?.id
    )

    allContracts.forEach((contract: IContract) => {
      const contractMOMSServices = countableByContractId.get(contract.id) ?? []
      console.debug(
        `${fn} ${contract.hrid}: ${contractMOMSServices.length} services`
      )
      contract.numOrderMSServices =
        sumCountableMSServicesQuantity(contractMOMSServices)
      contract.momssStatusCounts = countUniqueStatii(
        contractMOMSServices,
        getCountableMSServiceQuantity
      )

      // Set contract status and canBeDeleted based on numOrderMSServices
      // ⚠ OPEN QUESTION — SA / Non-Contract audit, 2026-08-20. Deferred, not
      // settled: this gates the NO_SERVICES <-> DRAFT transition on SA ALONE,
      // so a Non-Contract never gets its status re-derived here. The
      // OPPORTUNITY cascade runs the same rule for every non-MSA contract
      // (_propagateFromOMSServicesToContracts), i.e. Non-Contract included.
      // Company and Order agree with each other and disagree with Opportunity.
      //
      // Aligning them needs additional logic rather than a one-line edit, so
      // the divergence stands for now. All three behaviours are pinned by the
      // co-located specs, so changing any one of them will fail loudly.
      if (contract.contractType.id === ContractTypeIds.SA) {
        console.debug(
          `${fn} contractType is SA. Going to set status based on numOrderMSServices.`
        )
        if (!contract.numOrderMSServices) {
          contract.status = getContractStatusById(ContractStatusIds.NO_SERVICES)
          contract.canBeDeleted = true
        } else if (contract.status.id === ContractStatusIds.NO_SERVICES) {
          contract.status = getContractStatusById(ContractStatusIds.DRAFT)
          contract.canBeDeleted = false
        }
      }

      // Set doLockAcceptedBySupplierDate
      contract.doLockAcceptedBySupplierDate = contractMOMSServices.some(
        (momss) =>
          (momss.status.id as number) >
          OrderMSServicesStatusIds.READY_FOR_SERVICEORDER
      )
    })
    console.debug(`${fn} propagated to ${allContracts.length} contracts`)
  }
  function _propagateFromMOMSServicesToOrderServiceOrders({
    mn,
    allOrderServiceOrders,
    allMOMSServices,
  }: {
    mn: string
    allOrderServiceOrders: IOrderServiceOrder[]
    allMOMSServices: TMSService[]
  }) {
    const fn = `${mn} _propagateFromMOMSServicesToOrderServiceOrders |`
    let didChangeOrderServiceOrderStatus = false // KLUDGEY WAY TO HANDLE THIS. NEED TO CHANGE. JR

    // Same one-pass index as the contracts stage above. This was the second
    // O(entities x msServices) loop in this cascade.
    const countableByServiceOrderId = groupCountableByKey(
      allMOMSServices,
      (momss) => momss.orderServiceOrder?.id
    )

    allOrderServiceOrders.forEach((orderServiceOrder: IOrderServiceOrder) => {
      const orderServiceOrderMOMSServices =
        countableByServiceOrderId.get(orderServiceOrder.id) ?? []
      console.debug(
        `${fn} ${orderServiceOrder.hrid}:` +
          ` ${orderServiceOrderMOMSServices.length} services`
      )
      orderServiceOrder.numOrderMSServices = sumCountableMSServicesQuantity(
        orderServiceOrderMOMSServices
      )

      // Set orderServiceOrder status

      const oldOrderServiceOrderStatusId = orderServiceOrder.status.id

      if (!orderServiceOrder.numOrderMSServices) {
        // Set orderServiceOrder status based on numOrderMSServices (no services)
        orderServiceOrder.status = getOrderServiceOrderStatusById(
          OrderServiceOrderStatusIds.NO_SERVICES
        )
      } else {
        // Set orderServiceOrder status based on services' statii
        const propagateStatusArray: TPotentialStatus[] = [
          {
            statusId: OrderMSServicesStatusIds.SERVICEORDER_IN_PROGRESS,
            statusIdForOnlyMssStatus: OrderServiceOrderStatusIds.IN_PROGRESS,
            statusIdForOtherFound: OrderServiceOrderStatusIds.IN_PROGRESS,
          },
          {
            statusId: OrderMSServicesStatusIds.ACCEPTED,
            statusIdForOnlyMssStatus: OrderServiceOrderStatusIds.ACCEPTED,
            statusIdForOtherFound: OrderServiceOrderStatusIds.SOME_ACCEPTED,
          },
          {
            statusId: OrderMSServicesStatusIds.COMPLETE,
            statusIdForOnlyMssStatus: OrderServiceOrderStatusIds.COMPLETE,
            statusIdForOtherFound: OrderServiceOrderStatusIds.SOME_COMPLETE,
          },
          // {
          //   statusId: OrderMSServicesStatusIds.CANCELLED,
          //   statusIdForOnlyMssStatus: OrderServiceOrderStatusIds.CANCELLED,
          //   statusIdForOtherFound: OrderServiceOrderStatusIds.SOME_CANCELLED,
          // },
        ]

        orderServiceOrder.status =
          getTargetEntityStatusFromPropagateStatusArray<IOrderServiceOrder>({
            propagateStatusArray,
            momssArray: orderServiceOrderMOMSServices,
            targetEntity: orderServiceOrder,
            targetEntityGetStatusByIdFunction: getOrderServiceOrderStatusById,
          })
      }

      if (orderServiceOrder.status.id !== oldOrderServiceOrderStatusId) {
        console.debug(
          `${fn} orderServiceOrder '${orderServiceOrder.hrid}' status.id` +
            ` changed from ${oldOrderServiceOrderStatusId} to` +
            ` ${orderServiceOrder.status.id}`
        )
        didChangeOrderServiceOrderStatus = true
      }

      // Status counts

      orderServiceOrder.momssStatusCounts = countUniqueStatii(
        orderServiceOrderMOMSServices,
        getCountableMSServiceQuantity
      )

      // Aging

      orderServiceOrder.doesExceedNumDaysToLateAmber = false
      orderServiceOrder.doesExceedNumDaysToLateRed = false

      // Neither `today` nor `created` depends on the msService, so both are
      // hoisted out of the loop they used to be rebuilt inside.
      const today = new Date()
      assertIsString(orderServiceOrder.createdAt)
      const created = new Date(orderServiceOrder.createdAt)
      const numDaysSinceCreation = Math.round(
        (today.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)
      )
      for (const momss of orderServiceOrderMOMSServices) {
        if (
          !momss.product.numDaysToLateAmber ||
          !momss.product.numDaysToLateRed
        ) {
          continue
        }
        if (numDaysSinceCreation < 1) {
          console.debug(
            `${fn} numDaysSinceCreation is ${numDaysSinceCreation}.` +
              ` Not calculating numDaysToLate properties.`
          )
          break
        }
        console.debug(
          `${fn} numDaysSinceCreation: ${numDaysSinceCreation}.` +
            ` numDaysToLate: ${momss.product.numDaysToLateAmber}` +
            ` ${momss.product.numDaysToLateRed}`
        )
        if (numDaysSinceCreation > momss.product.numDaysToLateAmber) {
          console.debug(`${fn} setting doesExceedNumDaysToLateAmber = true`)
          orderServiceOrder.doesExceedNumDaysToLateAmber = true
          // If we find one of these, we still have to check the
          // rest for exceeding the LateRed threshold
        }
        if (numDaysSinceCreation > momss.product.numDaysToLateRed) {
          console.debug(`${fn} setting doesExceedNumDaysToLateRed = true`)
          orderServiceOrder.doesExceedNumDaysToLateRed = true
          break // If we find one of these, we're done
        }
      }
    })
    console.debug(
      `${fn} propagated to ${allOrderServiceOrders.length} service orders`
    )
    return didChangeOrderServiceOrderStatus // PART OF KLUDGE JR
  }

  //
  // execution starts here...
  //
  const fn = `${mn} propagateMOMSServicesData |`
  console.debug(`${fn} called`)
  setUpOrderMSServices()
  _propagateFromMOMSServicesToOrder({
    mn,
    order: overviewData.order,
    allMOMSServices,
  })
  _propagateFromMOMSServicesToMOMS({
    mn,
    allMOMS: overviewData.orderMasterServices,
  })
  _propagateFromMOMSServicesToContracts({
    mn,
    allContracts: overviewData.contracts,
    allMOMSServices,
  })
  const orderServiceOrderStatusChanged =
    _propagateFromMOMSServicesToOrderServiceOrders({
      mn,
      allOrderServiceOrders: overviewData.orderServiceOrders,
      allMOMSServices,
    })
  // KLUDGE to get the SO status counts in order to update on change to
  // orderServiceOrder.status propagated from orderMSServices.
  // Because orderServiceOrder status change is just a result of
  // propagation, it does -not- trigger the orderServiceOrder
  // childUpdated method (there's no API call) so we have
  // to force the orderServiceOrder propagation to run manually.
  // Also, can't simply watch overview.orderServiceOrders and trigger
  // a handler cause this results in an endless loop of propagation
  // handlers calling each other.
  // Probably should use the publish/subscribe pattern but I don't
  // want to take the time to implement now.JR.
  if (orderServiceOrderStatusChanged) {
    console.debug(
      `${fn} orderServiceOrderStatusChanged is true, so` +
        ` calling propagateOrderServiceOrderData`
    )
    // DEBUG AID — uncomment to snapshot the whole overview at this instant.
    //
    // The cloneDeep is the point, not an accident: overviewData keeps being
    // mutated after this line (propagateOrderServiceOrderData runs next, and the
    // caller propagates again after that), and DevTools renders an object
    // lazily. Logging it live therefore shows you its END state, not its state
    // here. The clone freezes it.
    //
    // Left commented out because it is expensive enough to matter on a large
    // order — a full deep copy of every master service, msService, contract,
    // contact, document and note, on every service-order status change. It also
    // needs `import { cloneDeep } from 'lodash'` restored at the top of the file.
    //
    // console.debug(`${fn} overviewData snapshot:`, cloneDeep(overviewData))
    propagateOrderServiceOrderData({ callingFn: fn, overviewData })
  }
}

function getTargetEntityStatusFromPropagateStatusArray<
  T extends IEntityDataItemWithStatus
>({
  propagateStatusArray,
  momssArray,
  targetEntity,
  targetEntityGetStatusByIdFunction,
}: {
  propagateStatusArray: TPotentialStatus[]
  momssArray: TMSService[]
  targetEntity: T
  targetEntityGetStatusByIdFunction: Function
}) {
  function _gotIdButNotObjectErrorHandler({
    potentialStatus,
    targetEntity,
  }: {
    potentialStatus: TPotentialStatus
    targetEntity: T
  }) {
    throw new Error(
      `${fn} target Entity '${targetEntity.hrid}' -` +
        ` determined id of status to set` +
        ` (${potentialStatus.statusIdForOnlyMssStatus}),` +
        ` but couldn't get status object based on it.` +
        ` This should never happen.`
    )
  }
  const fn = `${mn} getTargetEntityStatusFromPropagateStatusArray |`

  // Both predicates are pure functions of the SET of distinct status ids
  // present, so one pass answers every rung. This used to be nine iterations
  // each running two `.some()` scans of the full array — eighteen whole-array
  // scans per entity, all computed eagerly to feed a loop that returns on the
  // first match. Order pays that twice over: once for the order itself and
  // once per master service.
  // ⚠ Superseded rows contribute nothing, and this `continue` is the only thing
  // that makes it so. Left in, such a row would join the set and make
  // "something other than this status" true on every rung, pinning the parent
  // at a SOME_* status forever -- a wrong roll-up no user action could clear.
  //
  // ⚠ It matters MORE than it used to. The marker is an itemAvailabilityState
  // now, so the row carries a real, ordinary lifecycle status: drop this and it
  // contributes a plausible status rather than an obviously foreign one, and
  // the ladder comes out subtly wrong instead of visibly stuck.
  //
  // ⚠ The server does NOT do this. Its status-propagation trigger aggregates
  // over every row with no availability filter at all, so client and server can
  // disagree here; filed as WXTG/cynque-api-server#1569.
  const presentStatusIds = new Set<number>()
  for (const momss of momssArray) {
    if (isSupersededMSService(momss)) {
      continue
    }
    presentStatusIds.add(momss.status.id as number)
  }

  propagateStatusArray.forEach((potentialStatus) => {
    potentialStatus.didFindStatus = presentStatusIds.has(
      potentialStatus.statusId
    )
    // "Something other than this status or the terminal one." Terminal here is
    // CANCELLED, not DISCONNECTED as in the Company copy of this function.
    potentialStatus.didFindOtherThanStatusOrCancelled = [
      ...presentStatusIds,
    ].some(
      (statusId) =>
        statusId !== potentialStatus.statusId &&
        statusId !== OrderMSServicesStatusIds.CANCELLED
    )
  })

  for (const potentialStatus of propagateStatusArray) {
    if (
      potentialStatus.didFindStatus &&
      !potentialStatus.didFindOtherThanStatusOrCancelled
    ) {
      const statusToSet = targetEntityGetStatusByIdFunction(
        potentialStatus.statusIdForOnlyMssStatus
      )
      if (!statusToSet) {
        _gotIdButNotObjectErrorHandler({ potentialStatus, targetEntity })
      }
      console.debug(`${fn} returning statusToSet ${statusToSet?.label}`)
      return statusToSet
    } else if (
      potentialStatus.didFindStatus &&
      potentialStatus.didFindOtherThanStatusOrCancelled
    ) {
      const statusToSet = targetEntityGetStatusByIdFunction(
        potentialStatus.statusIdForOtherFound
      )
      if (!statusToSet) {
        _gotIdButNotObjectErrorHandler({ potentialStatus, targetEntity })
      }
      console.debug(`${fn} returning statusToSet ${statusToSet?.label}`)
      return statusToSet
    }
  }
  throw new Error(
    `${fn} target Entity with hrid '${targetEntity.hrid}' status did not get set.` +
      ` This should never happen.`
  )
}
